import React from 'react';
import { Alert, Box, Chip, Paper, Stack, Typography, alpha, useTheme } from '@mui/material';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import type { DialOpDemandMover, DialOpDemandShift } from '../../../types/dialingOpportunity';
import { buildDialOpGridSx, DIAL_OP_PAGE_SIZE_OPTIONS, DialOpGridToolbar, HeaderWithTooltip } from './dialOpTableKit';

const DEMAND_BASELINE_WEEKS = 8;
const SHIFT_SINGLE_STATE_PP = 5;
const SHIFT_TOTAL_MOVEMENT_PP = 12;

function formatPct(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${value.toFixed(1)}%`;
}

function formatDelta(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  const sign = value >= 0 ? '+' : '−';
  return `${sign}${Math.abs(value).toFixed(1)} pts`;
}

interface DemandShiftTableProps {
  shift: DialOpDemandShift;
  /** Render without the outer Paper/title chrome (for use inside a section). */
  bare?: boolean;
  /** How many states the global State filter currently has selected. */
  selectedStateCount?: number;
}

export default function DemandShiftTable({
  shift,
  bare = false,
  selectedStateCount = 0,
}: DemandShiftTableProps) {
  const theme = useTheme();

  const gridRows = React.useMemo(
    () =>
      shift.movers.map((row, index) => ({
        id: row.state_cd ?? `row-${index}`,
        state_cd: row.state_cd ?? '—',
        baseline_pct: row.baseline_pct,
        current_pct: row.current_pct,
        delta_pp: row.delta_pp,
        abs_delta_pp: Math.abs(row.delta_pp),
      })),
    [shift.movers],
  );

  const columns = React.useMemo<GridColDef[]>(
    () => [
      {
        field: 'state_cd',
        headerName: 'State',
        minWidth: 72,
        flex: 0.6,
        sortable: true,
        renderHeader: () => (
          <HeaderWithTooltip label="State" tooltip="The lead's state." />
        ),
      },
      {
        field: 'baseline_pct',
        headerName: `Prior ${DEMAND_BASELINE_WEEKS} wks`,
        type: 'number',
        minWidth: 120,
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        sortable: true,
        renderHeader: () => (
          <HeaderWithTooltip
            label={`Prior ${DEMAND_BASELINE_WEEKS} wks`}
            tooltip={`This state's share of all first calls to new leads, averaged over the ${DEMAND_BASELINE_WEEKS} weeks before your selected period.`}
          />
        ),
        valueFormatter: (value: number | null) => formatPct(value),
      },
      {
        field: 'current_pct',
        headerName: 'This period',
        type: 'number',
        minWidth: 110,
        flex: 1,
        align: 'right',
        headerAlign: 'right',
        sortable: true,
        renderHeader: () => (
          <HeaderWithTooltip
            label="This period"
            tooltip="This state's share of all first calls to new leads in your selected period."
          />
        ),
        valueFormatter: (value: number | null) => formatPct(value),
      },
      {
        field: 'delta_pp',
        headerName: 'Change',
        type: 'number',
        minWidth: 110,
        flex: 0.9,
        align: 'right',
        headerAlign: 'right',
        sortable: true,
        renderHeader: () => (
          <HeaderWithTooltip
            label="Change"
            tooltip="Percentage-point change in this state's share of first calls vs the prior 8 weeks."
          />
        ),
        renderCell: (params: GridRenderCellParams<DialOpDemandMover & { id: string }>) => {
          const delta = params.value === null || params.value === undefined ? null : Number(params.value);
          if (delta === null) return '—';
          const notable = Math.abs(delta) >= SHIFT_SINGLE_STATE_PP;
          return (
            <Box
              component="span"
              sx={{
                fontWeight: notable ? 700 : 500,
                color: notable ? theme.palette.warning.dark : 'text.primary',
              }}
            >
              {formatDelta(delta)}
            </Box>
          );
        },
      },
    ],
    [theme.palette.warning.dark],
  );

  const gridSx = React.useMemo(() => buildDialOpGridSx(theme), [theme]);

  if (!shift.movers.length) {
    return null;
  }

  const intro = (
    <Box sx={bare ? { px: 2.5, pt: 2 } : { px: 2.5, py: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
      {bare ? null : (
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1" fontWeight={700}>
            Lead geography mix
          </Typography>
          {shift.flagged ? (
            <Chip
              size="small"
              color="warning"
              variant="outlined"
              icon={<WarningAmberRounded sx={{ fontSize: 15 }} />}
              label="Shift detected"
              sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: 12 } }}
            />
          ) : (
            <Chip
              size="small"
              variant="outlined"
              label="Within normal range"
              sx={{ height: 22, '& .MuiChip-label': { px: 0.75, fontSize: 12 } }}
            />
          )}
        </Stack>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mt: bare ? 0 : 0.5 }}>
        Where your leads are coming from — each row is a state&apos;s share of all first calls to new
        leads, this period vs. the prior {DEMAND_BASELINE_WEEKS} weeks. This is about incoming lead
        demand, not calling quality. Sorted by largest shift.
      </Typography>
      {selectedStateCount === 1 ? (
        <Alert severity="info" sx={{ mt: 1.5, py: 0.75 }}>
          Only one state is selected above, so its share is always 100% — there&apos;s no mix to
          compare. Select 2 or more states (or clear the filter) to see a real shift.
        </Alert>
      ) : shift.flagged ? (
        <Alert
          severity="info"
          icon={<InfoOutlined fontSize="small" />}
          sx={{ mt: 1.5, py: 0.75, '& .MuiAlert-message': { width: '100%' } }}
        >
          <Typography variant="body2" sx={{ lineHeight: 1.5 }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
              Shift detected
            </Box>{' '}
            when any single state moves ≥{SHIFT_SINGLE_STATE_PP} percentage points of first-call
            share, or when combined movement across the states shown adds up to ≥
            {SHIFT_TOTAL_MOVEMENT_PP} points. This period: largest single move{' '}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {shift.max_delta_pp.toFixed(1)} pts
            </Box>
            ; combined movement{' '}
            <Box component="span" sx={{ fontWeight: 600 }}>
              {shift.total_abs_delta_pp.toFixed(1)} pts
            </Box>
            .
          </Typography>
        </Alert>
      ) : null}
    </Box>
  );

  const grid = (
    <Box sx={{ width: '100%', minHeight: 320 }}>
      <DataGrid
        rows={gridRows}
        columns={columns}
        disableRowSelectionOnClick
        sortingMode="client"
        paginationMode="client"
        pageSizeOptions={DIAL_OP_PAGE_SIZE_OPTIONS}
        localeText={{ toolbarQuickFilterPlaceholder: 'Search states…' }}
        initialState={{
          sorting: { sortModel: [{ field: 'abs_delta_pp', sort: 'desc' }] },
          pagination: { paginationModel: { pageSize: 10, page: 0 } },
        }}
        columnVisibilityModel={{ abs_delta_pp: false }}
        slots={{ toolbar: DialOpGridToolbar }}
        sx={gridSx}
        autoHeight
      />
    </Box>
  );

  if (bare) {
    return (
      <>
        {intro}
        {grid}
      </>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={(t) => ({
        overflow: 'hidden',
        borderRadius: `${t.ds.radius.md}px`,
        border: `1px solid ${shift.flagged ? t.palette.warning.main : t.tokens.border.subtle}`,
        ...(shift.flagged ? { bgcolor: alpha(t.palette.warning.main, 0.03) } : {}),
      })}
    >
      {intro}
      {grid}
    </Paper>
  );
}
